import { useEffect, useState, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, ChevronLeft, ArrowRight } from 'lucide-react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { TypeAnimation } from 'react-type-animation';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import ScrollReveal, { StaggerItem } from '../components/ScrollReveal';
import TextReveal from '../components/TextReveal';
import { useParallax } from '../hooks/useScrollAnimation';
import { useModal } from '../App';
import Footer from '../components/Footer';
import AnimatedCounter from '../components/AnimatedCounter';
import HeroCarousel from '../components/HeroCarousel';
import { LogoCloud } from '../components/LogoCloud';
import { supabase } from '../lib/supabase';
import { fetchAllContent } from '../lib/content';
import { useI18n } from '../lib/i18n';
import SEOHead from '../components/SEOHead';
import { Helmet } from 'react-helmet-async';

gsap.registerPlugin(ScrollTrigger);


const Home = ({ autoOpenModal }) => {
  const [news, setNews] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [metrics, setMetrics] = useState([]);
  const [sponsorLogos, setSponsorLogos] = useState([]);
  const [partnerLogos, setPartnerLogos] = useState([]);
  const [supporterLogos, setSupporterLogos] = useState([]);
  const [institutionalLogos, setInstitutionalLogos] = useState([]);
  const [dbProjects, setDbProjects] = useState([]);
  const [publications, setPublications] = useState([]);
  const parallaxRef = useParallax(0.25);
  const { openModal } = useModal();
  const { t, tf, lang } = useI18n();
  const pt = lang === 'pt';

  // Auto-open modal from legacy routes
  useEffect(() => {
    if (!autoOpenModal) return;
    if (autoOpenModal === 'article-url') {
      // Extract slug and open parent portal with slug hint
      const slug = window.location.pathname.split('/').filter(Boolean).pop();
      const path = window.location.pathname;
      const portal = path.startsWith('/artigos') ? 'artigos' : 'emfoco';
      const timer = setTimeout(() => openModal(`${portal}:${slug}`), 100);
      return () => clearTimeout(timer);
    } else {
      const timer = setTimeout(() => openModal(autoOpenModal), 100);
      return () => clearTimeout(timer);
    }
  }, [autoOpenModal, openModal]);

  // Fetch from Supabase
  useEffect(() => {
    supabase.from('impact_metrics').select('*').eq('section', 'home').order('sort_order').then(({ data }) => data && setMetrics(data));
    supabase.from('partners').select('*').eq('active', true).order('sort_order').then(({ data }) => {
      if (!data) return;
      const toLogos = (items) => items.map((p) => ({ src: p.logo_url || '', alt: p.name }));
      setSponsorLogos(toLogos(data.filter((p) => p.type === 'patrocinador')));
      setPartnerLogos(toLogos(data.filter((p) => p.type === 'parceiro')));
      setSupporterLogos(toLogos(data.filter((p) => p.type === 'apoiador')));
      setInstitutionalLogos(toLogos(data.filter((p) => p.type === 'apoiador_institucional')));
    });
    supabase.from('publications').select('*').order('created_at').then(({ data }) => data && setPublications(data));
    supabase.from('projects').select('*').eq('active', true).order('sort_order').then(({ data }) => {
      if (!data || data.length === 0) return;
      setDbProjects(data);
    });
  }, []);

  const { scrollY } = useScroll();
  const heroOpacity = useTransform(scrollY, [0, 600], [1, 0]);
  const heroScale = useTransform(scrollY, [0, 600], [1, 1.1]);
  const heroTextY = useTransform(scrollY, [0, 400], [0, 80]);

  // SEOHead handles title via react-helmet-async

  useEffect(() => {
    fetchAllContent({ type: 'noticia', limit: 6 }).then((data) => {
      setNews(data || []);
    });
  }, []);

  useEffect(() => {
    if (news.length <= 3) return;
    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % (news.length - 2));
    }, 5000);
    return () => clearInterval(timer);
  }, [news.length]);

  const nextSlide = () => {
    if (news.length <= 3) return;
    setCurrentIndex((prev) => (prev + 1) % (news.length - 2));
  };

  const prevSlide = () => {
    if (news.length <= 3) return;
    setCurrentIndex((prev) => (prev - 1 + (news.length - 2)) % (news.length - 2));
  };

  const getVisibleNews = () => {
    if (news.length <= 3) return news;
    return news.slice(currentIndex, currentIndex + 3);
  };

  const orgJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Instituto BR Cultural',
    url: 'https://institutobrcultural.org',
    logo: 'https://institutobrcultural.org/logo192.png',
    description: 'Há 19 anos formando talentos, desenvolvendo tecnologia e acelerando negócios para a economia criativa brasileira.',
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Rua Teodoro Sampaio, 1109',
      addressLocality: 'Pinheiros, São Paulo',
      addressRegion: 'SP',
      addressCountry: 'BR',
    },
    contactPoint: {
      '@type': 'ContactPoint',
      email: 'contato@institutobrcultural.org',
      contactType: 'customer service',
    },
  };

  return (
    <div>
      <SEOHead
        title="Instituto BR Cultural"
        description="Há 19 anos formando talentos, desenvolvendo tecnologia e acelerando negócios para a economia criativa brasileira."
        url="https://institutobrcultural.org"
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(orgJsonLd)}</script>
      </Helmet>
      {/* Hero Section */}
      <section className="relative h-screen bg-black overflow-hidden" data-testid="home-hero-section">
        <motion.div className="absolute inset-0" style={{ scale: heroScale }}>
          <HeroCarousel />
        </motion.div>

        <motion.div
          className="relative z-10 h-full flex flex-col container mx-auto px-4 md:px-8"
          style={{ opacity: heroOpacity, y: heroTextY }}
        >
          <div className="flex-1" />
          <div className="text-center mb-12">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
            >
              <div className="section-divider mx-auto mb-8" />
              <div className="font-oswald text-sm md:text-lg lg:text-xl text-[#FFC107] uppercase tracking-[0.3em] mb-4 h-7">
                <TypeAnimation
                  sequence={[
                    'Cinema', 1800,
                    'IA', 1800,
                    'Games', 1800,
                    'Aceleração', 1800,
                    'Mercado', 1800,
                    'P&D', 1800,
                  ]}
                  wrapper="span"
                  speed={30}
                  repeat={Infinity}
                  cursor={false}
                  style={{ display: 'inline-block' }}
                />
              </div>
              <h1
                className="font-oswald text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-white leading-tight mb-6 hero-text-glow"
                data-testid="hero-title"
              >
                {t('hero.line1', 'há 19 anos movendo')} {t('hero.line2', 'quem cria')}
              </h1>
            </motion.div>
            <motion.p
              className="font-manrope text-base md:text-lg lg:text-xl text-white/80 max-w-4xl mx-auto leading-relaxed"
              data-testid="hero-subtitle"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
            >
              <span className="block font-semibold text-lg md:text-xl lg:text-2xl mb-3 text-[#FFC107]">
                {t('hero.highlight', 'Cultura, tecnologia e educação.')}
              </span>
              {t('hero.desc', 'Três forças que o IBRC integra para transformar a economia criativa no mundo.')}
            </motion.p>
          </div>
          <motion.div
            className="pb-28 md:pb-16 flex justify-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9 }}
          >
            <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
              <button
                onClick={() => openModal('projetos')}
                className="h-10 px-8 bg-[#FFC107]/70 backdrop-blur-sm text-black uppercase tracking-widest text-[11px] font-semibold hover:bg-white/80 transition-all flex items-center justify-center gap-2 btn-spring border-none cursor-pointer"
                data-testid="hero-cta-button"
              >
                {t('hero.cta', 'CONHEÇA NOSSA ATUAÇÃO')}
                <ArrowRight size={20} className="hidden md:block" aria-hidden="true" />
              </button>
            </motion.div>
          </motion.div>
        </motion.div>

        {/* Scroll indicator */}
        <motion.div
          className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10"
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
        >
          <div className="w-[1px] h-12 bg-gradient-to-b from-white/0 via-white/50 to-white/0" />
        </motion.div>
      </section>

      {/* News Carousel Section */}
      <section className="py-16 md:py-24 bg-white" data-testid="home-news-section">
        <div className="container mx-auto px-4 md:px-8 max-w-[1400px]">
          <ScrollReveal preset="fade-up">
            <div className="mb-12 md:mb-16">
              <div className="section-divider mb-6" />
              <button onClick={() => openModal('emfoco')} className="inline-block group bg-transparent border-none p-0 cursor-pointer text-left">
                <h2
                  className="font-oswald text-3xl md:text-5xl uppercase mb-4 group-hover:text-[#FFC107] transition-colors"
                  data-testid="news-section-title"
                >
                  {t('news.title', 'Em Foco')}
                  <span className="inline-block ml-3 opacity-0 group-hover:opacity-100 transition-opacity text-[#FFC107]">→</span>
                </h2>
              </button>
              <p
                className="font-manrope text-lg text-black/50"
                data-testid="news-section-subtitle"
              >
                {t('news.subtitle', 'Por dentro da indústria criativa do Brasil e do mundo')}
              </p>
            </div>
          </ScrollReveal>

          <div className="relative">
            {/* Previous Button */}
            {news.length > 3 && (
              <motion.button
                onClick={prevSlide}
                aria-label="Notícia anterior"
                className="hidden md:flex absolute -left-6 top-1/2 -translate-y-1/2 z-10 w-12 h-12 bg-white border border-black/10 items-center justify-center hover:bg-[#FFC107] hover:border-[#FFC107] transition-all shadow-lg"
                data-testid="news-carousel-prev"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
              >
                <ChevronLeft size={24} aria-hidden="true" />
              </motion.button>
            )}

            {/* News Grid */}
            <ScrollReveal preset="fade-up" stagger={0.12} delay={0.1}>
              <div className="overflow-x-auto md:overflow-hidden -mx-4 px-4 md:mx-0 md:px-2">
                <div className="flex md:grid md:grid-cols-3 gap-6 md:gap-8 transition-opacity duration-500 w-max md:w-auto">
                  {getVisibleNews().map((item) => (
                    <StaggerItem key={item.id}>
                      <Link to={`/noticias/${item.slug}`} className="w-[70vw] max-w-[288px] md:w-auto flex-shrink-0 block">
                        <div className="group cursor-pointer card-hover" data-testid={`news-card-${item.id}`}>
                          <div className="aspect-[4/3] overflow-hidden mb-4 md:mb-6">
                            <img
                              src={item.image}
                              alt={item[`title_${lang}`] || item.title_pt || ''}
                              className="w-full h-full object-cover grayscale-image"
                              loading="lazy"
                              decoding="async"
                            />
                          </div>
                          <p
                            className="text-[#FFC107] font-oswald text-sm uppercase tracking-wider mb-2"
                            data-testid={`news-category-${item.id}`}
                          >
                            {item.category}
                          </p>
                          <h3
                            className="font-oswald text-lg md:text-2xl uppercase mb-2 md:mb-3 group-hover:text-[#FFC107] transition-colors duration-300 line-clamp-2"
                            data-testid={`news-title-${item.id}`}
                          >
                            {item[`title_${lang}`] || item.title_pt || ''}
                          </h3>
                          <p
                            className="font-manrope text-black/50 text-sm line-clamp-2 hidden md:block"
                            data-testid={`news-excerpt-${item.id}`}
                          >
                            {item[`excerpt_${lang}`] || item.excerpt_pt || ''}
                          </p>
                        </div>
                      </Link>
                    </StaggerItem>
                  ))}

                  {/* Skeleton loaders when no news */}
                  {news.length === 0 && [1, 2, 3].map((i) => (
                    <div key={i} className="w-[70vw] max-w-[288px] md:w-auto flex-shrink-0">
                      <div className="aspect-[4/3] skeleton mb-6" />
                      <div className="h-4 w-20 skeleton mb-3" />
                      <div className="h-6 w-full skeleton mb-2" />
                      <div className="h-4 w-3/4 skeleton" />
                    </div>
                  ))}
                </div>
              </div>
            </ScrollReveal>

            {/* Next Button */}
            {news.length > 3 && (
              <motion.button
                onClick={nextSlide}
                aria-label="Próxima notícia"
                className="hidden md:flex absolute -right-6 top-1/2 -translate-y-1/2 z-10 w-12 h-12 bg-white border border-black/10 items-center justify-center hover:bg-[#FFC107] hover:border-[#FFC107] transition-all shadow-lg"
                data-testid="news-carousel-next"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
              >
                <ChevronRight size={24} aria-hidden="true" />
              </motion.button>
            )}
          </div>

          {/* Carousel Indicators */}
          {news.length > 3 && (
            <div className="hidden md:flex justify-center gap-2 mt-12 mb-8">
              {Array.from({ length: news.length - 2 }).map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={`h-2 rounded-full transition-all duration-500 ${
                    index === currentIndex ? 'bg-[#FFC107] w-10' : 'bg-black/20 w-2 hover:bg-black/40'
                  }`}
                  data-testid={`news-carousel-dot-${index}`}
                />
              ))}
            </div>
          )}

          <ScrollReveal preset="fade-up" delay={0.3}>
            <div className="text-center mt-12">
              <button
                onClick={() => openModal('emfoco')}
                className="inline-flex items-center gap-2 font-oswald uppercase text-sm tracking-widest hover:text-[#FFC107] transition-colors accent-line pb-1 bg-transparent border-none cursor-pointer"
                data-testid="news-view-more-button"
              >
                {t('news.viewmore', 'Ver Mais')}
                <ChevronRight size={16} />
              </button>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* ═══════ IMPACTO — Animated Counters ═══════ */}
      <section className="py-24 md:py-32 bg-black text-white overflow-hidden relative grain-overlay">
        <div className="container mx-auto px-4 md:px-8 max-w-[1400px] relative z-10">
          <div className="mb-16 md:mb-20">
            <div className="section-divider mb-6" />
            <TextReveal className="font-oswald text-3xl md:text-4xl text-white/90 uppercase">
              {t('impact.title', 'Impacto mensurável')}
            </TextReveal>
          </div>
          <ScrollReveal preset="fade-up" stagger={0.12}>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12">
              {(metrics.length ? metrics.map(m => ({ end: m.value, suffix: m.suffix, label: tf(m, 'label') })) : [
                { end: 19, suffix: '', label: 'Anos de atuação' },
                { end: 9, suffix: '', label: 'Frentes ativas' },
                { end: 3000, suffix: '+', label: 'Livros na biblioteca' },
                { end: 2000, suffix: '+', label: 'Títulos na filmoteca' },
              ]).map((stat, i) => (
                <StaggerItem key={i}>
                  <div className="text-center md:text-left">
                    <span className="font-oswald text-5xl md:text-7xl text-[#FFC107] block mb-3">
                      <AnimatedCounter end={stat.end} suffix={stat.suffix} />
                    </span>
                    <span className="font-manrope text-white/40 text-xs md:text-sm uppercase tracking-[0.15em]">
                      {stat.label}
                    </span>
                  </div>
                </StaggerItem>
              ))}
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* ═══════ PROJETOS — Carousel ═══════ */}
      <ProjectsCarousel dbProjects={dbProjects} />

      {/* ═══════ PATROCINADORES, PARCEIROS & APOIADORES ═══════ */}
      <section className="py-20 md:py-28 bg-white border-t border-black/5">
        <div className="container mx-auto px-4 md:px-8 max-w-[1400px]">
          <ScrollReveal preset="fade-up">
            <div className="text-center mb-16">
              <h2 className="font-oswald text-2xl md:text-3xl uppercase tracking-[0.08em] text-black/80 mb-2">
                {t('partners.title', 'Quem apoia e realiza conosco')}
              </h2>
              <div className="w-12 h-[2px] bg-[#FFC107] mx-auto" />
            </div>
          </ScrollReveal>
          {sponsorLogos.length > 0 && (
            <>
              <ScrollReveal preset="fade-up" delay={0.05}>
                <div className="text-center mb-8">
                  <span className="font-manrope text-xs uppercase tracking-[0.2em] text-black/30 mb-3 block">
                    {t('partners.sponsors', 'Patrocinadores')}
                  </span>
                  <div className="w-10 h-[2px] bg-[#FFC107]/50 mx-auto" />
                </div>
              </ScrollReveal>
              <ScrollReveal preset="fade-up" delay={0.08}>
                <LogoCloud logos={sponsorLogos} speed={15} size="lg" className="mb-12" />
              </ScrollReveal>
            </>
          )}
          {partnerLogos.length > 0 && (
            <>
              <ScrollReveal preset="fade-up" delay={0.1}>
                <div className="text-center mb-8">
                  <span className="font-manrope text-xs uppercase tracking-[0.2em] text-black/30 mb-3 block">
                    {t('partners.partners', 'Parceiros')}
                  </span>
                  <div className="w-10 h-[2px] bg-[#FFC107]/50 mx-auto" />
                </div>
              </ScrollReveal>
              <ScrollReveal preset="fade-up" delay={0.15}>
                <LogoCloud logos={partnerLogos} speed={12} reverse className="mb-12" />
              </ScrollReveal>
            </>
          )}
          {supporterLogos.length > 0 && (
            <>
              <ScrollReveal preset="fade-up" delay={0.2}>
                <div className="text-center mb-8">
                  <span className="font-manrope text-xs uppercase tracking-[0.2em] text-black/30 mb-3 block">
                    {t('partners.supporters', 'Apoiadores')}
                  </span>
                  <div className="w-10 h-[2px] bg-[#FFC107]/30 mx-auto" />
                </div>
              </ScrollReveal>
              <ScrollReveal preset="fade-up" delay={0.25}>
                <LogoCloud logos={supporterLogos} speed={12} className="mb-12" />
              </ScrollReveal>
            </>
          )}
          {institutionalLogos.length > 0 && (
            <>
              <ScrollReveal preset="fade-up" delay={0.3}>
                <div className="text-center mb-8">
                  <span className="font-manrope text-xs uppercase tracking-[0.2em] text-black/30 mb-3 block">
                    {t('partners.institutional', 'Apoiadores Institucionais')}
                  </span>
                  <div className="w-10 h-[2px] bg-[#FFC107]/20 mx-auto" />
                </div>
              </ScrollReveal>
              <ScrollReveal preset="fade-up" delay={0.35}>
                <LogoCloud logos={institutionalLogos} speed={10} size="lg" reverse />
              </ScrollReveal>
            </>
          )}
        </div>
      </section>

      {/* ═══════ PESQUISA & PUBLICAÇÕES — Jornal ═══════ */}
      <NewspaperSection publications={publications} openModal={openModal} t={t} tf={tf} lang={lang} />

      {/* Footer */}
      <Footer />
    </div>
  );
};

const projectsData = [
  {
    id: 'a360',
    title: 'A360',
    tag: { pt: 'Qualificação Global', en: 'Global Training' },
    desc: { pt: 'Até 792h de formação audiovisual gratuita. Patrocínio Netflix, Amazon MGM Studios e Globoplay. Padrão internacional.', en: 'Up to 792h of free audiovisual training. Sponsored by Netflix, Amazon MGM Studios and Globoplay. International standard.' },
    img: 'https://uuvsbgxrndwcnudctjlw.supabase.co/storage/v1/object/public/publications/covers/a360-card.webp',
    featured: false,
  },
  {
    id: 'fabrica',
    title: { pt: 'Fábrica de Conteúdos', en: 'Content Factory' },
    tag: { pt: 'Estúdio-Escola', en: 'Studio-School' },
    desc: { pt: 'Pipeline de produção inspirado no cinema industrial. Documentários, séries, podcasts e milhares de horas de cursos.', en: 'Production pipeline inspired by industrial cinema. Documentaries, series, podcasts and thousands of hours of courses.' },
    img: 'https://uuvsbgxrndwcnudctjlw.supabase.co/storage/v1/object/public/site-images/img_0796-hdr.webp',
  },
  {
    id: 'hub-sampa-games',
    title: 'Hub Sampa Games',
    tag: 'Games',
    desc: { pt: 'Polo de criação de jogos digitais. Programação, game design, arte e narrativa interativa.', en: 'Digital game creation hub. Programming, game design, art and interactive narrative.' },
    img: 'https://uuvsbgxrndwcnudctjlw.supabase.co/storage/v1/object/public/site-images/h.sampagames-37-(1).webp',
  },
  {
    id: 'amplificacine',
    title: 'AmplificaCine',
    tag: { pt: 'Aceleração', en: 'Acceleration' },
    desc: { pt: 'R$50 mil por empresa acelerada. Gestão, finanças, modelagem de negócios e pitch.', en: 'R$50K per accelerated company. Management, finance, business modeling and pitch.' },
    img: 'https://uuvsbgxrndwcnudctjlw.supabase.co/storage/v1/object/public/site-images/amplifica_kickoff-2026_-00047.webp',
  },
  {
    id: 'sororidade-conecta',
    title: 'Sororidade Conecta',
    tag: { pt: 'Empreendedorismo', en: 'Entrepreneurship' },
    desc: { pt: '500+ conteúdos exclusivos. Ecossistema phygital para empreendedorismo feminino.', en: '500+ exclusive contents. Phygital ecosystem for female entrepreneurship.' },
    img: 'https://images.unsplash.com/photo-1573164713714-d95e436ab8d6?w=900&q=80',
  },
  {
    id: 'publicus',
    title: 'PUBLICUS',
    tag: { pt: 'Escola de IA', en: 'AI School' },
    desc: { pt: 'IA é bem público. Letramento, agent coding, governança agêntica e teoria crítica.', en: 'AI is a public good. Literacy, agent coding, agentic governance and critical theory.' },
    img: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=900&q=80',
  },
  {
    id: 'cria',
    title: 'CRIA',
    tag: { pt: 'Pesquisa', en: 'Research' },
    desc: { pt: 'ICT credenciada. Acesso direto FINEP, EMBRAPII e FNDCT. 4 linhas de pesquisa.', en: 'Accredited ICT. Direct access to FINEP, EMBRAPII and FNDCT. 4 research lines.' },
    img: 'https://images.unsplash.com/photo-1532094349884-543bc11b234d?w=900&q=80',
  },
];

const i18nVal = (val, lang) => (typeof val === 'object' && val !== null ? (val[lang] || val.pt || '') : val);

const ProjectCard = ({ project, onClick, lang }) => {
  const title = i18nVal(project.title, lang);
  const tag = i18nVal(project.tag, lang);
  const desc = i18nVal(project.desc, lang);
  const pt = lang === 'pt';

  return (
    <div
      onClick={onClick}
      className="group flex-shrink-0 snap-start block cursor-pointer"
      style={{ width: project.featured ? 'clamp(360px, 42vw, 560px)' : 'clamp(300px, 36vw, 480px)' }}
    >
      <div className="relative bg-white p-3 md:p-4 border border-black/5 group-hover:border-[#FFC107]/30 transition-all duration-500 group-hover:shadow-[0_20px_60px_rgba(0,0,0,0.08)]">
        <div className="absolute top-6 left-6 md:top-7 md:left-7 z-20">
          <span className="font-oswald text-[10px] md:text-xs uppercase tracking-[0.2em] text-white/70 group-hover:text-[#FFC107] transition-colors duration-500 mix-blend-difference">
            {tag}
          </span>
        </div>
        <div className="relative aspect-[4/5] overflow-hidden">
          <img
            src={project.img}
            alt={title}
            className="w-full h-full object-cover grayscale-image"
            loading="lazy"
            decoding="async"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex flex-col justify-end p-5 md:p-7">
            <div className="transform translate-y-4 group-hover:translate-y-0 transition-transform duration-500">
              <h3 className="font-oswald text-2xl md:text-3xl uppercase text-white mb-3 leading-tight">
                {title}
              </h3>
              <p className="font-manrope text-white/70 text-sm leading-relaxed mb-4 line-clamp-3">
                {desc}
              </p>
              <span className="inline-flex items-center gap-2 font-oswald text-xs uppercase tracking-widest text-[#FFC107]">
                {pt ? 'Saiba mais' : 'Learn more'} <ArrowRight size={14} />
              </span>
            </div>
          </div>
        </div>
        <div className="pt-4 pb-1 group-hover:opacity-0 transition-opacity duration-300">
          <h3 className="font-oswald text-lg md:text-xl uppercase text-black">
            {title}
          </h3>
        </div>
      </div>
    </div>
  );
};

const ProjectsCarousel = ({ dbProjects = [] }) => {
  const scrollRef = useRef(null);
  const [isHovering, setIsHovering] = useState(false);
  const { openModal } = useModal();
  const { lang } = useI18n();
  const pt = lang === 'pt';

  // Merge DB data over hardcoded defaults (DB image_url overrides hardcoded img)
  const mergedProjects = projectsData.map((p) => {
    const dbMatch = dbProjects.find((d) => d.id === p.id);
    if (!dbMatch) return p;
    return { ...p, img: dbMatch.image_url || p.img, desc: dbMatch.description || p.desc, tag: dbMatch.tag || p.tag };
  });

  const items = [...mergedProjects, ...mergedProjects, ...mergedProjects];
  const cardWidth = 480;
  const gap = 32;
  const setWidth = projectsData.length * (cardWidth + gap);

  // Start at middle set
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollLeft = setWidth;
  }, [setWidth]);

  // Infinite loop repositioning
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const handleScroll = () => {
      if (el.scrollLeft >= setWidth * 2 - el.clientWidth) {
        el.style.scrollBehavior = 'auto';
        el.scrollLeft -= setWidth;
        requestAnimationFrame(() => { el.style.scrollBehavior = 'smooth'; });
      }
      if (el.scrollLeft <= 5) {
        el.style.scrollBehavior = 'auto';
        el.scrollLeft += setWidth;
        requestAnimationFrame(() => { el.style.scrollBehavior = 'smooth'; });
      }
    };
    el.addEventListener('scroll', handleScroll, { passive: true });
    return () => el.removeEventListener('scroll', handleScroll);
  }, [setWidth]);

  // Auto-play — faster, pauses on hover
  useEffect(() => {
    if (isHovering) return;
    const el = scrollRef.current;
    if (!el) return;
    const timer = setInterval(() => {
      el.style.scrollBehavior = 'auto';
      el.scrollLeft += 1.5;
    }, 16);
    return () => clearInterval(timer);
  }, [isHovering]);

  const scroll = useCallback((direction) => {
    const el = scrollRef.current;
    if (!el) return;
    // Animate manually for snappy feel
    const target = el.scrollLeft + direction * (cardWidth + gap);
    const start = el.scrollLeft;
    const diff = target - start;
    const duration = 400;
    let startTime = null;
    const step = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const ease = 1 - Math.pow(1 - progress, 3); // cubic ease-out
      el.scrollLeft = start + diff * ease;
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, []);

  return (
    <section
      className="py-16 md:py-24 bg-white overflow-hidden relative"
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      {/* Carousel */}
      <div
        ref={scrollRef}
        tabIndex={0}
        role="region"
        aria-label="Carrossel de projetos"
        className="flex gap-6 md:gap-8 overflow-x-auto pb-6 px-4 md:px-8 scrollbar-hide"
        style={{ WebkitOverflowScrolling: 'touch' }}
      >
        {items.map((project, i) => (
          <ProjectCard key={`${i18nVal(project.title, lang)}-${i}`} project={project} lang={lang} onClick={() => openModal(`projeto-${project.id}`)} />
        ))}
      </div>

      {/* Overlay Arrows — centered vertically on the carousel, over the edges */}
      <motion.button
        onClick={() => scroll(-1)}
        className="hidden md:flex absolute left-4 lg:left-8 top-1/2 -translate-y-1/2 z-20 w-14 h-14 items-center justify-center transition-all duration-300"
        style={{
          background: 'rgba(255, 193, 7, 0.15)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          border: '1px solid rgba(255, 193, 7, 0.3)',
        }}
        whileHover={{
          background: 'rgba(255, 193, 7, 0.9)',
          scale: 1.05,
        }}
        whileTap={{ scale: 0.93 }}
        aria-label={pt ? 'Anterior' : 'Previous'}
        initial={{ opacity: 0 }}
        animate={{ opacity: isHovering ? 1 : 0.4 }}
        transition={{ duration: 0.3 }}
      >
        <ChevronLeft size={24} className="text-black" />
      </motion.button>

      <motion.button
        onClick={() => scroll(1)}
        className="hidden md:flex absolute right-4 lg:right-8 top-1/2 -translate-y-1/2 z-20 w-14 h-14 items-center justify-center transition-all duration-300"
        style={{
          background: 'rgba(255, 193, 7, 0.15)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          border: '1px solid rgba(255, 193, 7, 0.3)',
        }}
        whileHover={{
          background: 'rgba(255, 193, 7, 0.9)',
          scale: 1.05,
        }}
        whileTap={{ scale: 0.93 }}
        aria-label={pt ? 'Próximo' : 'Next'}
        initial={{ opacity: 0 }}
        animate={{ opacity: isHovering ? 1 : 0.4 }}
        transition={{ duration: 0.3 }}
      >
        <ChevronRight size={24} className="text-black" />
      </motion.button>

      {/* Thin progress line */}
      <div className="mx-4 md:mx-8 mt-4">
        <div className="h-[1px] bg-black/5 relative overflow-hidden">
          <motion.div
            className="h-full bg-[#FFC107]/60 absolute left-0 top-0"
            style={{ width: `${(1 / projectsData.length) * 100}%` }}
            animate={{ x: [0, `${100 * (projectsData.length - 1)}%`] }}
            transition={{ duration: projectsData.length * 5, repeat: Infinity, ease: 'linear' }}
          />
        </div>
      </div>
    </section>
  );
};

// ── Newspaper Section ─────────────────────────────────────
// Layout estilo jornal agrupado por categoria
const NEWSPAPER_CATEGORIES = ['Educacao', 'Cultura', 'Tecnologia', 'Pesquisa', 'Institucional'];
const CATEGORY_LABELS = { Educacao: 'Educação', Cultura: 'Cultura', Tecnologia: 'Tecnologia', Pesquisa: 'Pesquisa', Institucional: 'Institucional' };

function NewspaperSection({ publications, openModal, t, tf, lang }) {
  // Group publications by category
  const grouped = {};
  const items = publications.length ? publications : [];

  for (const pub of items) {
    const cat = pub.category || 'Institucional';
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(pub);
  }

  // Get categories that have content, preserving order
  const activeCats = NEWSPAPER_CATEGORIES.filter((c) => grouped[c]?.length);

  const openArticle = (pub) => {
    if (pub.slug) {
      openModal(`emfoco:${pub.slug}`);
    } else if (pub.pdf_url || pub.link) {
      window.open(pub.pdf_url || pub.link, '_blank');
    }
  };

  const formatDate = (d) => {
    if (!d) return '';
    return new Date(d).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
  };

  // Featured: first 4 items (any category) for the top row
  const allSorted = items.slice(0, 4);
  const remaining = items.slice(4);

  // Regroup remaining by category
  const remainGrouped = {};
  for (const pub of remaining) {
    const cat = pub.category || 'Institucional';
    if (!remainGrouped[cat]) remainGrouped[cat] = [];
    remainGrouped[cat].push(pub);
  }
  const remainCats = NEWSPAPER_CATEGORIES.filter((c) => remainGrouped[c]?.length);

  return (
    <section className="py-16 md:py-24 bg-[#faf9f6] border-t border-black/5">
      <div className="container mx-auto px-4 md:px-8 max-w-[1200px]">

        {/* Header */}
        <ScrollReveal preset="fade-up">
          <div className="flex items-end justify-between mb-8 border-b-2 border-black pb-3">
            <div>
              <h2 className="font-oswald text-3xl md:text-4xl uppercase text-black leading-none">
                {t('publications.title', 'Pesquisa & Publicações')}
              </h2>
              <p className="font-serif italic text-sm text-black/40 mt-1">
                {t('publications.quote', 'Produzir conhecimento é responsabilidade de quem quer transformar.')}
              </p>
            </div>
            <button
              onClick={() => openModal('emfoco')}
              className="hidden md:flex items-center gap-1.5 font-manrope text-[10px] uppercase tracking-wider text-[#FFC107] hover:text-black transition-colors flex-shrink-0"
            >
              Ver tudo <ArrowRight size={12} />
            </button>
          </div>
        </ScrollReveal>

        {/* Top row — Featured 4 cards (image top, text bottom) */}
        {allSorted.length > 0 && (
          <ScrollReveal preset="fade-up" delay={0.05}>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
              {allSorted.map((pub, i) => {
                const title = pub[`title_${lang}`] || pub.title_pt || pub.title || '';
                return (
                  <article
                    key={pub.id || i}
                    className="group cursor-pointer"
                    onClick={() => openArticle(pub)}
                  >
                    <div className="aspect-[4/3] overflow-hidden bg-black/5 mb-3">
                      {pub.image ? (
                        <img
                          src={pub.image}
                          alt={title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                          decoding="async"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-black/10 to-black/5 flex items-center justify-center">
                          <span className="font-oswald text-3xl text-black/5 uppercase">{(pub.category || 'I')[0]}</span>
                        </div>
                      )}
                    </div>
                    <h3 className="font-manrope text-sm text-black/80 leading-snug group-hover:text-[#FFC107] transition-colors line-clamp-3">
                      {title}
                    </h3>
                    {pub.author && (
                      <span className="font-manrope text-[10px] text-black/25 mt-1 block">{pub.author}</span>
                    )}
                  </article>
                );
              })}
            </div>
          </ScrollReveal>
        )}

        {/* Category columns — 2 per row, image-left text-right */}
        {remainCats.length > 0 && (
          <ScrollReveal preset="fade-up" delay={0.1}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-10">
              {remainCats.map((cat) => (
                <div key={cat}>
                  {/* Category header */}
                  <h3 className="font-oswald text-xl md:text-2xl uppercase text-black mb-4 pb-2 border-b-2 border-[#FFC107]">
                    {CATEGORY_LABELS[cat] || cat}
                  </h3>

                  {/* Articles in this category */}
                  <div className="space-y-4">
                    {remainGrouped[cat].slice(0, 3).map((pub, i) => {
                      const title = pub[`title_${lang}`] || pub.title_pt || pub.title || '';
                      const excerpt = pub[`excerpt_${lang}`] || pub.excerpt_pt || '';
                      const catLabel = pub.content_type === 'artigo' ? 'Artigo' : (pub.category || '');
                      return (
                        <article
                          key={pub.id || i}
                          className="flex gap-3 group cursor-pointer"
                          onClick={() => openArticle(pub)}
                        >
                          {/* Thumbnail */}
                          <div className="w-24 h-20 flex-shrink-0 overflow-hidden bg-black/5">
                            {pub.image ? (
                              <img
                                src={pub.image}
                                alt={title}
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                loading="lazy"
                                decoding="async"
                                onError={(e) => { e.target.style.display = 'none'; }}
                              />
                            ) : (
                              <div className="w-full h-full bg-gradient-to-br from-black/10 to-black/5" />
                            )}
                          </div>

                          {/* Text */}
                          <div className="flex-1 min-w-0">
                            <span className="font-oswald text-[9px] uppercase tracking-[0.15em] text-[#FFC107] block mb-0.5">
                              {catLabel}
                            </span>
                            <h4 className="font-manrope text-sm font-semibold text-black/80 leading-snug group-hover:text-[#FFC107] transition-colors line-clamp-2">
                              {title}
                            </h4>
                            {excerpt && (
                              <p className="font-manrope text-[11px] text-black/35 line-clamp-1 mt-0.5">{excerpt}</p>
                            )}
                          </div>
                        </article>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </ScrollReveal>
        )}

        {/* Empty state */}
        {items.length === 0 && (
          <ScrollReveal preset="fade-up">
            <div className="text-center py-16">
              <p className="font-serif italic text-black/25 text-lg mb-4">
                {t('publications.quote', 'Produzir conhecimento é responsabilidade de quem quer transformar.')}
              </p>
              <p className="font-manrope text-black/30 text-sm max-w-[500px] mx-auto">
                {t('publications.desc', 'O IBRC pesquisa, testa e publica. Nossos relatórios de impacto, artigos técnicos e análises de mercado estão disponíveis para consulta pública.')}
              </p>
              <p className="font-manrope text-black/15 text-xs mt-6 uppercase tracking-wider">
                {t('publications.coming', 'Publicações em breve')}
              </p>
            </div>
          </ScrollReveal>
        )}

        {/* Mobile: Ver tudo */}
        <div className="md:hidden mt-8 text-center">
          <button
            onClick={() => openModal('emfoco')}
            className="inline-flex items-center gap-2 font-manrope text-xs uppercase tracking-wider text-[#FFC107]"
          >
            Ver todas as publicações <ArrowRight size={12} />
          </button>
        </div>
      </div>
    </section>
  );
}

export default Home;
